// ============================================================
// InvitationsPage.js
// Authenticated page at /invitations. Lists invitations with Status
// and Client filters and Previous/Next pagination.
//
// State machine for the main fetch (loadingState):
//   'loading'     — initial mount, no data yet, full-page spinner
//   'refetching'  — re-fetch after a filter or pagination change;
//                   keeps the existing table visible (dimmed) so the
//                   user doesn't see a flash of "no data"
//   'loaded'      — data ready, no fetch in flight (may be empty)
//   'error'       — last fetch failed, no/stale data
//
// Transition into 'refetching' vs 'loading' is computed via the
// functional setState form: if we already have data displayed
// ('loaded' or 'refetching'), use 'refetching'; otherwise (initial
// mount, prior error, retry from error) use 'loading'.
//
// loadData's useCallback deps include statusFilter, clientIdFilter,
// and offset — same idiom as commit 1's [loadData] effect dep, so
// React's effect graph re-runs the fetch automatically when filters
// or pagination change. Lint-clean and stale-closure-safe.
//
// Clients dropdown is populated by a separate listClients() fetch
// running in its own useEffect with [] deps. Independent loading
// and error states; failure falls back to "All clients" only so the
// page stays usable even if listClients() is broken.
//
// Chrome (brand + sign-out) is owned by AppShell/SideNav — this
// page renders content only.
// ============================================================

import React, { useCallback, useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, X, UserPlus, CheckCircle2 } from 'lucide-react';
import { issueInvitation, listInvitations, revokeInvitation, resendInvitation } from '../api/invitations';
import { listClients } from '../api/clients';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import useMediaQuery from '../hooks/useMediaQuery';
import useDelayedRefresh from '../hooks/useDelayedRefresh';
import EmailDeliveryNote from '../components/EmailDeliveryNote';
import './InvitationsPage.css';

const PAGE_LIMIT = 50;

// Human-readable role labels. Backend emits the lowercase keys in the
// roleKey field. Unknown values fall back to `<raw>?` so backend drift
// (a new role we forgot to add here) is visible in production rather
// than silently rendering blank.
const ROLE_LABELS = {
  manager: 'Manager',
  carer:   'Carer',
  admin:   'Admin',
  auditor: 'Auditor',
  // The G1/G2 care-record access roles (careplan-access-invitations)
  // share this table; before 2026-09-03 they rendered as the raw key
  // with a question mark on every row.
  service_user:          'Service user',
  representative:        'Representative',
  external_professional: 'External professional',
};

function formatRole(roleKey) {
  return ROLE_LABELS[roleKey] || `${roleKey}?`;
}

// Branches on status:
//   pending  → relative ("Expires in 5 days" / "Expires in 7h" /
//              defensive "Expired" if expiresAt is past)
//   anything else → absolute en-GB date ("10 May 2026")
//
// en-GB locale chosen specifically — admin operators are UK-based.
// Uses the browser's Intl, which is reliable across all supported
// browsers (the CLAUDE.md ICU notes apply to the Functions runtime,
// not the browser).
function formatExpiry(expiresAt, status) {
  if (!expiresAt) return '';
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return '';

  if (status !== 'pending') {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  }

  const diffMs = date.getTime() - Date.now();
  if (diffMs < 0) return 'Expired';
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return `Expires in ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
}

// "Reminded twice, last 10 Sep" for a pending/expired row the reminder
// timer has emailed; empty for everything else. The count comes from
// the automatic timer only — a manual resend stamps the date but does
// not count as a reminder, so an operator reading "reminded 3 times"
// knows nobody has spoken to this person yet.
function formatReminders(invitation) {
  const count = invitation.reminderCount || 0;
  if (count === 0) return '';
  const last = invitation.lastReminderAt ? new Date(invitation.lastReminderAt) : null;
  const lastText = last && !Number.isNaN(last.getTime())
    ? `, last ${last.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : '';
  const times = count === 1 ? 'once' : count === 2 ? 'twice' : `${count} times`;
  return `Reminded ${times}${lastText}`;
}

// Resend is offered for the two states a fresh link can help with:
// stored-pending (still live, the person lost the email) and
// computed-expired (the link lapsed — the resend gives a new window).
// The backend's own gate is the stored status, so both map to 200.
function canResend(invitation) {
  return invitation.status === 'pending' || invitation.status === 'expired';
}

export default function InvitationsPage() {
  const navigate = useNavigate();

  // Below --breakpoint-md the seven-column table becomes a card list.
  // Matches the shared DataTable's breakpoint; this page predates that
  // component and still owns its own table.
  const cardMode = useMediaQuery('(max-width: 768px)');

  // Filter + pagination state.
  // null sentinels mean "no filter applied"; mapped to undefined
  // params when calling listInvitations (so the API client's
  // query-string builder skips them).
  const [statusFilter, setStatusFilter] = useState(null);
  const [clientIdFilter, setClientIdFilter] = useState(null);
  const [offset, setOffset] = useState(0);

  // Invitations fetch state.
  const [invitations, setInvitations] = useState([]);
  const [loadingState, setLoadingState] = useState('loading');
  const [error, setError] = useState(null);

  // Clients fetch state — independent from the invitations fetch so
  // a failure on either side doesn't block the other.
  const [clients, setClients] = useState([]);
  const [clientsLoadingState, setClientsLoadingState] = useState('loading');

  // Issue-invitation modal + submit state. submitError holds the
  // ApiError from the last failed submit so the form can surface it;
  // successBanner holds the data for the inline confirmation strip
  // above the filter bar (auto-dismisses in 8s, replaced on subsequent
  // issues — no stacking).
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting]         = useState(false);
  const [submitError, setSubmitError]           = useState(null);
  const [successBanner, setSuccessBanner]       = useState(null);

  // Revoke-invitation modal + submit state. revokingInvitation holds
  // the row clicked in the table (drives both whether the modal is
  // open and what data the confirmation message shows). Kept separate
  // from Issue's state so the two flows never collide if both ever
  // open concurrently (currently can't, but state stays clean).
  const [revokingInvitation, setRevokingInvitation] = useState(null);
  const [isRevoking, setIsRevoking]                 = useState(false);
  const [revokeError, setRevokeError]               = useState(null);

  // Resend is a one-click row action (no confirm — it is reversible in
  // the sense that the worst case is one extra email). resendingId
  // disables the clicked row's button while the request is in flight;
  // resendError renders inline above the list and clears on the next
  // action or refetch.
  const [resendingId, setResendingId] = useState(null);
  const [resendError, setResendError] = useState(null);

  const loadData = useCallback(async () => {
    // 'loaded' or 'refetching' → 'refetching' (keep existing rows
    // visible). Anything else (initial 'loading', prior 'error') →
    // 'loading' (full-page spinner).
    setLoadingState((prev) =>
      prev === 'loaded' || prev === 'refetching' ? 'refetching' : 'loading'
    );
    setError(null);
    setResendError(null);
    try {
      const response = await listInvitations({
        clientId: clientIdFilter ?? undefined,
        status:   statusFilter ?? undefined,
        limit:    PAGE_LIMIT,
        offset,
      });
      setInvitations(response?.invitations || []);
      setLoadingState('loaded');
    } catch (err) {
      // Already logged by the api/client.js wrapper. Just store + surface.
      setError(err);
      setLoadingState('error');
    }
  }, [statusFilter, clientIdFilter, offset]);
  const scheduleDeliveryRefresh = useDelayedRefresh(loadData);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Clients fetch — runs once on mount. Failures degrade gracefully:
  // the dropdown falls back to "All clients" only.
  useEffect(() => {
    (async () => {
      try {
        // Pull a generous page; small client lists in practice. If we
        // ever exceed PAGE_LIMIT clients, the dropdown becomes a
        // typeahead concern (out of scope here).
        const response = await listClients({ limit: 200 });
        setClients(response?.clients || []);
        setClientsLoadingState('loaded');
      } catch {
        setClientsLoadingState('error');
      }
    })();
  }, []);

  // Open clears any stale submitError so a previous-attempt error
  // never bleeds into a fresh form. Close also clears, so both
  // entry/exit paths are clean — covers the mid-submit-then-close-
  // then-API-errors-after race where the failure would otherwise
  // hang on submitError state with no visible surface.
  // Kept as fallback per Session 3 locked decision #4. The route
  // /invitations/new is the new canonical issue path; the embedded
  // modal stays for safety until smoke confirms the route works.
  // eslint-disable-next-line no-unused-vars
  const handleOpenIssueModal = () => {
    setSubmitError(null);
    setIsIssueModalOpen(true);
  };
  const handleCloseIssueModal = () => {
    setIsIssueModalOpen(false);
    setSubmitError(null);
  };

  const dismissBanner = useCallback(() => setSuccessBanner(null), []);

  // Real submit: calls issueInvitation, on success closes the modal,
  // shows the inline success banner, and refreshes the invitation
  // list (keeping current filters intact — predictable beats clever,
  // the banner confirms the issuance even if filters exclude the new
  // pending row).
  //
  // On error: keep the modal open, surface via submitError; form data
  // stays intact so the user can correct + retry. submitError is
  // cleared at the start of every attempt and on modal close.
  //
  // If the user closes mid-submit, isIssueModalOpen flips to false
  // while the request is still in flight. The promise still resolves
  // — success then sets successBanner + loadData (visible feedback);
  // failure sets submitError into a closed modal (silently lost, by
  // design — no place to surface it without surprising the user).
  const handleIssueInvitationSubmit = async (formData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await issueInvitation(formData);
      setIsIssueModalOpen(false);
      const clientName =
        clients.find((c) => c.clientId === formData.clientId)?.clientName
        || 'the selected client';
      const email       = result?.email       || formData.email;
      const displayName = result?.displayName || formData.displayName;
      // Pass a React fragment (not a string) so email + clientName
      // render in <strong> — matches the Phase 4 banner's original
      // emphasis. Phase 5's Revoke refactor narrowed the contract
      // to "string only" in commentary; the actual render is
      // {banner.message} which has always accepted ReactNode.
      // Revoke caller below stays as a plain string — only Issue
      // benefits from the emphasis here.
      setSuccessBanner({
        message: (
          <>Invitation issued to <strong>{email}</strong> ({displayName}) at <strong>{clientName}</strong>.</>
        ),
      });
      loadData();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open the revoke confirmation modal. Clears any stale revokeError
  // so a previous-attempt error never bleeds into a fresh confirmation.
  const handleOpenRevokeModal = (invitation) => {
    setRevokeError(null);
    setRevokingInvitation(invitation);
  };

  // Close clears both the target invitation and any error. Mirrors
  // Phase 4's handleCloseIssueModal hygiene.
  const handleCloseRevokeModal = () => {
    setRevokingInvitation(null);
    setRevokeError(null);
  };

  // Real revoke: POSTs to mgmt/invitations/{id}/revoke. On success
  // closes the modal, shows the inline banner, and refreshes the
  // invitations list. On error keeps the modal open with the error
  // surface so the operator can read it and retry.
  //
  // Mid-submit close (Escape/backdrop) is allowed per Phase 4
  // precedent — operator's choice. The request still completes;
  // success shows the banner regardless of modal state. Failure
  // after close silently drops (no UI to surface it).
  const handleRevokeInvitation = async () => {
    if (!revokingInvitation) return;
    setIsRevoking(true);
    setRevokeError(null);
    try {
      const result = await revokeInvitation(revokingInvitation.invitationId);
      const email = revokingInvitation.email;
      // wasIdempotent: true means another admin (or our own earlier
      // click) already revoked this row — backend short-circuited
      // with a 200 no-op. Surface that distinction to the operator.
      const verb = result?.wasIdempotent ? 'was already revoked' : 'revoked';
      setRevokingInvitation(null);
      setSuccessBanner({
        message: `Invitation to ${email} ${verb}.`,
      });
      loadData();
    } catch (err) {
      setRevokeError(err);
    } finally {
      setIsRevoking(false);
    }
  };

  // Resend: POSTs to mgmt/invitations/{id}/resend, which rotates the
  // token, gives the invitation a fresh 30-day window, and re-sends the
  // branded email. Works on a computed-expired row too — that is the
  // whole point (before this button the only route was revoke + issue
  // again). Success shows the banner and refetches so the Expires
  // column moves; failure surfaces inline and the row stays clickable.
  const handleResendInvitation = async (invitation) => {
    setResendingId(invitation.invitationId);
    setResendError(null);
    try {
      await resendInvitation(invitation.invitationId);
      setSuccessBanner({
        message: (
          <>Invitation re-sent to <strong>{invitation.email}</strong>. The link in the new email is valid for 30 days; earlier links no longer work.</>
        ),
      });
      loadData();
      // A refusal by their mail server arrives seconds AFTER the send
      // is accepted, so re-read once more to surface it.
      scheduleDeliveryRefresh();
    } catch (err) {
      setResendError({ email: invitation.email, error: err });
    } finally {
      setResendingId(null);
    }
  };

  // Filter handlers — every filter change resets offset to 0 so the
  // user sees page 1 of the new filtered set, not "page 3 of
  // unfiltered" mapped to a now-shorter list. React 18's automatic
  // batching collapses these two state updates into a single render
  // and a single fetch.
  const handleStatusChange = (next) => {
    setStatusFilter(next);
    setOffset(0);
  };

  const handleClientChange = (next) => {
    setClientIdFilter(next);
    setOffset(0);
  };

  const handleClearFilters = () => {
    setStatusFilter(null);
    setClientIdFilter(null);
    setOffset(0);
  };

  const handlePrevious = () => {
    setOffset((o) => Math.max(0, o - PAGE_LIMIT));
  };

  const handleNext = () => {
    setOffset((o) => o + PAGE_LIMIT);
  };

  const hasActiveFilters = statusFilter !== null || clientIdFilter !== null;
  const emptyMessage = hasActiveFilters
    ? 'No invitations match these filters.'
    : 'No invitations yet.';

  // Both the empty branch and the table branch accept 'loaded' OR
  // 'refetching'. This means a refetch from an empty result keeps the
  // empty message visible; a refetch from a populated table keeps the
  // table visible (dimmed). Either way, no flash-of-blank.
  const hasData = loadingState === 'loaded' || loadingState === 'refetching';

  return (
    <>
      <PageHeader
        title="Invitations"
        subtitle="Issue and manage user invitations across all clients."
        cta={
          <button
            type="button"
            className="button-primary"
            onClick={() => navigate('/invitations/new')}
          >
            <UserPlus size={16} aria-hidden="true" />
            <span>Issue invitation</span>
          </button>
        }
      />

      {successBanner && (
        <SuccessBanner banner={successBanner} onDismiss={dismissBanner} />
      )}

      <FilterBar
        statusFilter={statusFilter}
        clientIdFilter={clientIdFilter}
        clients={clients}
        clientsLoadingState={clientsLoadingState}
        hasActiveFilters={hasActiveFilters}
        onStatusChange={handleStatusChange}
        onClientChange={handleClientChange}
        onClear={handleClearFilters}
      />

      {resendError && (
        <p className="form-error-summary invitations-page__action-error" role="alert">
          {resendFormLevelError(resendError)}
        </p>
      )}

      <section className="invitations-page__state" aria-live="polite">
        {loadingState === 'loading' && (
          <div className="state-message">
            <Loader2 className="spinner" size={20} aria-hidden="true" />
            <span>Loading invitations…</span>
          </div>
        )}

        {loadingState === 'error' && (
          <ErrorCard error={error} onRetry={loadData} />
        )}

        {hasData && invitations.length === 0 && (
          <p className="state-message state-message--empty">{emptyMessage}</p>
        )}

        {hasData && invitations.length > 0 && (
          <div
            className={
              'invitations-table-wrapper' +
              (loadingState === 'refetching' ? ' invitations-table-wrapper--refetching' : '')
            }
          >
            {cardMode ? (
              <ul className="invitations-cards">
                {invitations.map((inv) => (
                  <InvitationCard
                    key={inv.invitationId}
                    invitation={inv}
                    onRevoke={handleOpenRevokeModal}
                    onResend={handleResendInvitation}
                    resending={resendingId === inv.invitationId}
                  />
                ))}
              </ul>
            ) : (
              <table className="invitations-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Display name</th>
                    <th>Role</th>
                    <th>Client</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th className="actions-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => (
                    <InvitationRow
                      key={inv.invitationId}
                      invitation={inv}
                      onRevoke={handleOpenRevokeModal}
                      onResend={handleResendInvitation}
                      resending={resendingId === inv.invitationId}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {hasData && invitations.length > 0 && (
        <Pagination
          offset={offset}
          returned={invitations.length}
          limit={PAGE_LIMIT}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      )}

      <Modal
        isOpen={isIssueModalOpen}
        onClose={handleCloseIssueModal}
        title="Issue invitation"
        closeDisabled={isSubmitting}
      >
        {/* Conditional render so the form's local state is fresh on
            every open — no per-field reset wiring needed. */}
        {isIssueModalOpen && (
          <IssueInvitationForm
            clients={clients}
            clientsLoadingState={clientsLoadingState}
            isSubmitting={isSubmitting}
            submitError={submitError}
            onCancel={handleCloseIssueModal}
            onSubmit={handleIssueInvitationSubmit}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!revokingInvitation}
        onClose={handleCloseRevokeModal}
        title="Revoke invitation"
        closeDisabled={isRevoking}
      >
        {revokingInvitation && (
          <RevokeConfirmation
            invitation={revokingInvitation}
            isRevoking={isRevoking}
            revokeError={revokeError}
            onCancel={handleCloseRevokeModal}
            onRevoke={handleRevokeInvitation}
          />
        )}
      </Modal>
    </>
  );
}

function FilterBar({
  statusFilter, clientIdFilter, clients, clientsLoadingState,
  hasActiveFilters, onStatusChange, onClientChange, onClear,
}) {
  const clientsLoading = clientsLoadingState === 'loading';
  const clientsLoaded  = clientsLoadingState === 'loaded';

  return (
    <div className="filter-bar">
      <div className="filter-bar__field">
        <label htmlFor="status-filter">Status:</label>
        <select
          id="status-filter"
          className="filter-bar__select"
          value={statusFilter ?? ''}
          onChange={(e) => onStatusChange(e.target.value || null)}
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="redeemed">Redeemed</option>
          <option value="revoked">Revoked</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <div className="filter-bar__field">
        <label htmlFor="client-filter">Client:</label>
        <select
          id="client-filter"
          className="filter-bar__select"
          value={clientIdFilter ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            onClientChange(v === '' ? null : Number(v));
          }}
          disabled={clientsLoading}
        >
          {clientsLoading && (
            <option value="">Loading clients…</option>
          )}
          {!clientsLoading && (
            <option value="">All clients</option>
          )}
          {clientsLoaded && clients.map((c) => (
            <option key={c.clientId} value={c.clientId}>{c.clientName}</option>
          ))}
        </select>
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          className="filter-bar__clear"
          onClick={onClear}
        >
          <X size={14} aria-hidden="true" />
          <span>Clear filters</span>
        </button>
      )}
    </div>
  );
}

function Pagination({ offset, returned, limit, onPrevious, onNext }) {
  // No total count returned by the backend (agreed scope decision).
  // "Showing {first}–{last}" without a total. Next is disabled when
  // returned < limit (we definitely got the tail); enabled when
  // returned == limit (could be more, could be exactly limit — Next
  // revealing zero new rows is acceptable UX).
  const first = offset + 1;
  const last  = offset + returned;
  const prevDisabled = offset === 0;
  const nextDisabled = returned < limit;

  return (
    <div className="pagination">
      <span className="pagination__indicator">
        Showing {first}–{last}
      </span>
      <div className="pagination__buttons">
        <button
          type="button"
          className="pagination__button"
          onClick={onPrevious}
          disabled={prevDisabled}
        >
          Previous
        </button>
        <button
          type="button"
          className="pagination__button"
          onClick={onNext}
          disabled={nextDisabled}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function InvitationRow({ invitation, onRevoke, onResend, resending }) {
  const isPending = invitation.status === 'pending';
  const resendable = canResend(invitation);
  const reminders = formatReminders(invitation);
  return (
    <tr>
      <td className="invitations-table__email">{invitation.email}</td>
      <td>{invitation.displayName || ''}</td>
      <td>{formatRole(invitation.roleKey)}</td>
      <td>{invitation.clientName}</td>
      <td>
        <span className={`status-badge status-badge--${invitation.status}`}>
          {invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
        </span>
        {resendable && <EmailDeliveryNote lastEmail={invitation.lastEmail} />}
      </td>
      <td className="invitations-table__expires">
        <div>{formatExpiry(invitation.expiresAt, invitation.status)}</div>
        {reminders && <div className="invitations-table__reminders">{reminders}</div>}
      </td>
      <td className="actions-cell">
        <button
          type="button"
          className="resend-button"
          onClick={() => onResend(invitation)}
          disabled={!resendable || resending}
          title={resendable
            ? 'Send a fresh invitation email with a new 30-day link'
            : `Cannot resend a ${invitation.status} invitation`}
        >
          {resending ? 'Sending…' : 'Resend'}
        </button>
        <button
          type="button"
          className="revoke-button"
          onClick={() => onRevoke(invitation)}
          disabled={!isPending}
          title={isPending
            ? 'Revoke this invitation'
            : `Cannot revoke a ${invitation.status} invitation`}
        >
          Revoke
        </button>
      </td>
    </tr>
  );
}

// The phone face of a row.
//
// The table puts Revoke in the seventh and last column, which on a
// 390px screen means a full horizontal scroll of the table before you
// can reach the only action the page offers. Here it sits directly
// under the invitation it acts on.
//
// Email leads because it is what identifies an invitation — the
// display name is frequently blank, and every other field is context.
function InvitationCard({ invitation, onRevoke, onResend, resending }) {
  const isPending = invitation.status === 'pending';
  const resendable = canResend(invitation);
  const reminders = formatReminders(invitation);
  const statusLabel = invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1);
  return (
    <li className="invitations-card">
      <div className="invitations-card__head">
        <span className="invitations-card__email">{invitation.email}</span>
        <span className={`status-badge status-badge--${invitation.status}`}>
          {statusLabel}
        </span>
      </div>

      {invitation.displayName && (
        <div className="invitations-card__name">{invitation.displayName}</div>
      )}

      <dl className="invitations-card__meta">
        <div>
          <dt>Role</dt>
          <dd>{formatRole(invitation.roleKey)}</dd>
        </div>
        <div>
          <dt>Client</dt>
          <dd>{invitation.clientName}</dd>
        </div>
        <div>
          <dt>Expires</dt>
          <dd>{formatExpiry(invitation.expiresAt, invitation.status)}</dd>
        </div>
        {reminders && (
          <div>
            <dt>Reminders</dt>
            <dd>{reminders}</dd>
          </div>
        )}
        {resendable && invitation.lastEmail && (
          <div>
            <dt>Email</dt>
            <dd><EmailDeliveryNote lastEmail={invitation.lastEmail} /></dd>
          </div>
        )}
      </dl>

      {/* Rendered only while they can do something. A permanently
          disabled button on every redeemed row is noise on a surface
          where vertical space is the scarce resource; the status badge
          above already says why there is nothing to do. */}
      {(resendable || isPending) && (
        <div className="invitations-card__actions">
          {resendable && (
            <button
              type="button"
              className="resend-button invitations-card__resend"
              onClick={() => onResend(invitation)}
              disabled={resending}
            >
              {resending ? 'Sending…' : 'Resend'}
            </button>
          )}
          {isPending && (
            <button
              type="button"
              className="revoke-button invitations-card__revoke"
              onClick={() => onRevoke(invitation)}
            >
              Revoke
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// Mirrors the email regex in backend/.../admin-invitations-issue.js so
// client and server agree on what's valid. Looser regexes here would
// surface as 400s the user could have prevented; stricter ones would
// block legal addresses the backend would accept.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function validateEmail(value) {
  const v = value.trim();
  if (v.length === 0) return 'Email is required';
  if (v.length > 200) return 'Email must be 200 characters or fewer';
  if (!EMAIL_RE.test(v)) return 'Enter a valid email address';
  return '';
}

function validateDisplayName(value) {
  // Backend rule: trim().length >= 1 (required), raw length <= 200.
  if (value.trim().length === 0) return 'Display name is required';
  if (value.length > 200) return 'Display name must be 200 characters or fewer';
  return '';
}

function validateRoleKey(value) {
  if (!value) return 'Select a role';
  return '';
}

function validateClientId(value) {
  if (!value) return 'Select a client';
  return '';
}

// Translate an ApiError into a single form-level message — null when
// the error is field-scoped (those surface inline next to the input)
// or absent. Specific copy per known status; raw 500 detail is
// deliberately hidden from the operator (App Insights captures it).
function formLevelServerError(submitError) {
  if (!submitError) return null;
  // Field-scoped 400: not form-level. Returned as null; getFieldError
  // below pulls the message out for the input it belongs to.
  if (submitError.status === 400 && submitError.field) return null;
  if (submitError.isNetworkError) {
    return 'Could not reach the API. Check your connection and try again.';
  }
  if (submitError.status === 409) {
    return 'A pending invitation already exists for this email at this client. Revoke the existing invitation or use a different email.';
  }
  if (submitError.status === 500) {
    return 'Something went wrong on our end. Please try again.';
  }
  // Catch-all: 400 without field, 401/403, or anything else unexpected.
  return submitError.message || 'Something went wrong. Please try again.';
}

function IssueInvitationForm({
  clients,
  clientsLoadingState,
  isSubmitting,
  submitError,
  onCancel,
  onSubmit,
}) {
  const fieldId = useId();

  const [email, setEmail]             = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleKey, setRoleKey]         = useState('');
  const [clientId, setClientId]       = useState('');
  const [touched, setTouched]         = useState({
    email: false, displayName: false, roleKey: false, clientId: false,
  });

  // Errors are derived from current field values on every render — no
  // separate error state to keep in sync. Mirrors validateBody() in
  // backend/.../admin-invitations-issue.js.
  const errors = {
    email:       validateEmail(email),
    displayName: validateDisplayName(displayName),
    roleKey:     validateRoleKey(roleKey),
    clientId:    validateClientId(clientId),
  };

  const clientsLoading = clientsLoadingState === 'loading';
  const clientsErrored = clientsLoadingState === 'error';
  const clientsEmpty   = clientsLoadingState === 'loaded' && clients.length === 0;
  const clientsBlocked = clientsLoading || clientsErrored || clientsEmpty;

  const isValid =
    !errors.email && !errors.displayName && !errors.roleKey && !errors.clientId &&
    !clientsBlocked &&
    !isSubmitting;

  const markTouched = (field) =>
    setTouched((t) => (t[field] ? t : { ...t, [field]: true }));

  // Display logic for a field's error message:
  //   - server 400 with field === this field wins (it's the most recent
  //     truth about validity, and the user just hit submit so they're
  //     ready for feedback)
  //   - otherwise the client-side validation error, but only after the
  //     field has been blurred or a submit was attempted (touched).
  const getFieldError = (field) => {
    if (submitError && submitError.status === 400 && submitError.field === field) {
      return submitError.message;
    }
    if (touched[field] && errors[field]) {
      return errors[field];
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Reveal any errors on fields the user skipped before bailing.
    setTouched({ email: true, displayName: true, roleKey: true, clientId: true });
    if (!isValid) return;
    onSubmit({
      clientId:    Number(clientId),
      email:       email.trim(),
      displayName: displayName.trim(),
      roleKey,
    });
  };

  // Form-level alert above the fields for clients-fetch problems that
  // block the entire form. Distinct from per-field errors — these
  // aren't user-correctable, they're page-level state.
  const clientsAlert =
    clientsErrored ? 'Failed to load clients. Try refreshing the page.'
    : clientsEmpty ? 'No clients exist yet. Add a client before issuing invitations.'
    : null;

  // Client dropdown placeholder text varies with fetch state.
  const clientsPlaceholder =
    clientsLoading ? 'Loading clients…'
    : clientsErrored ? 'Failed to load clients'
    : clientsEmpty ? 'No clients available'
    : 'Select a client…';

  const serverFormError = formLevelServerError(submitError);

  const emailError       = getFieldError('email');
  const displayNameError = getFieldError('displayName');
  const roleKeyError     = getFieldError('roleKey');
  const clientIdError    = getFieldError('clientId');

  return (
    <form className="issue-form" onSubmit={handleSubmit} noValidate>
      {serverFormError && (
        <p className="form-error-summary" role="alert">{serverFormError}</p>
      )}

      {clientsAlert && (
        <p className="form-error-summary" role="alert">{clientsAlert}</p>
      )}

      <div className="form-field">
        <label htmlFor={`${fieldId}-email`} className="form-field__label">Email</label>
        <input
          id={`${fieldId}-email`}
          type="email"
          autoComplete="off"
          placeholder="user@example.com"
          autoFocus
          disabled={isSubmitting}
          className={'form-field__input' + (emailError ? ' form-field__input--error' : '')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => markTouched('email')}
        />
        {emailError && (
          <p className="form-field__error" role="alert">{emailError}</p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor={`${fieldId}-name`} className="form-field__label">Display name</label>
        <input
          id={`${fieldId}-name`}
          type="text"
          autoComplete="off"
          placeholder="Jane Smith"
          disabled={isSubmitting}
          className={'form-field__input' + (displayNameError ? ' form-field__input--error' : '')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          onBlur={() => markTouched('displayName')}
        />
        {displayNameError && (
          <p className="form-field__error" role="alert">{displayNameError}</p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor={`${fieldId}-role`} className="form-field__label">Role</label>
        <select
          id={`${fieldId}-role`}
          disabled={isSubmitting}
          className={'form-field__select' + (roleKeyError ? ' form-field__select--error' : '')}
          value={roleKey}
          onChange={(e) => setRoleKey(e.target.value)}
          onBlur={() => markTouched('roleKey')}
        >
          <option value="" disabled>Select a role…</option>
          <option value="manager">Manager</option>
          <option value="carer">Carer</option>
          <option value="admin">Admin</option>
          <option value="auditor">Auditor</option>
        </select>
        {roleKeyError && (
          <p className="form-field__error" role="alert">{roleKeyError}</p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor={`${fieldId}-client`} className="form-field__label">Client</label>
        <select
          id={`${fieldId}-client`}
          className={'form-field__select' + (clientIdError ? ' form-field__select--error' : '')}
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          onBlur={() => markTouched('clientId')}
          disabled={clientsBlocked || isSubmitting}
        >
          <option value="" disabled>{clientsPlaceholder}</option>
          {clientsLoadingState === 'loaded' && clients.map((c) => (
            <option key={c.clientId} value={c.clientId}>{c.clientName}</option>
          ))}
        </select>
        {clientIdError && (
          <p className="form-field__error" role="alert">{clientIdError}</p>
        )}
      </div>

      <div className="issue-form__actions">
        <button
          type="button"
          className="button-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button type="submit" className="button-primary" disabled={!isValid}>
          {isSubmitting ? (
            <>
              <Loader2 className="spinner" size={16} aria-hidden="true" />
              <span>Issuing…</span>
            </>
          ) : (
            <span>Issue invitation</span>
          )}
        </button>
      </div>
    </form>
  );
}

// Inline success strip rendered above the filter bar. Auto-dismisses
// after 8s. Replaced (not stacked) when a subsequent action fires —
// admin tool, simpler UX. The banner prop is just { message } — the
// message can be either a plain string OR a React node (e.g. a
// fragment with <strong> for emphasis on key terms like email /
// clientName). {banner.message} below renders both transparently.
// Callers compose the message so the banner stays generic and
// reusable across modules (issue, revoke, future delete/etc.);
// callers that want emphasis pass a fragment, callers that don't
// pass a string. Do NOT narrow this contract back to string-only —
// the Issue caller relies on the ReactNode path for its bolded
// email + clientName (the Phase 4 → Phase 5 narrowing dropped the
// bold, now restored). Truthy presence of `banner` is enforced by
// the parent rendering this component conditionally.
function SuccessBanner({ banner, onDismiss }) {
  useEffect(() => {
    const id = setTimeout(onDismiss, 8000);
    return () => clearTimeout(id);
  }, [banner, onDismiss]);

  return (
    <div className="success-banner" role="status">
      <div className="success-banner__icon" aria-hidden="true">
        <CheckCircle2 size={20} />
      </div>
      <p className="success-banner__message">{banner.message}</p>
      <button
        type="button"
        className="success-banner__dismiss"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}

// Translate a revoke ApiError into a form-level message. 404 covers
// "already revoked or deleted between page render and click" — a
// race with another admin or with the backend's own sweeper. 409
// covers both "already redeemed" and the rare lookup-then-update
// race in admin-invitations-revoke.js; both surface the backend's
// `detail` field so the operator sees the specific reason. 500 hides
// the raw detail (App Insights captures it).
function revokeFormLevelError(revokeError) {
  if (!revokeError) return null;
  if (revokeError.isNetworkError) {
    return 'Could not reach the API. Check your connection and try again.';
  }
  if (revokeError.status === 404) {
    return 'This invitation has already been revoked or no longer exists. Refresh the list to see the current state.';
  }
  if (revokeError.status === 409) {
    return revokeError.detail
        || revokeError.message
        || 'This invitation can’t be revoked right now. Refresh the list and try again.';
  }
  if (revokeError.status === 500) {
    return 'Something went wrong on our end. Please try again.';
  }
  return revokeError.message || 'Something went wrong. Please try again.';
}

// Translate a resend ApiError into the inline message. 409 is the
// backend's not_pending (the row was redeemed or revoked since the
// page loaded) or its lookup-then-update race; both carry `detail`.
// 500 means the token rotated but the email failed — the backend's
// own detail says the old link is dead and to retry, which is the
// one 500 whose detail the operator genuinely needs to read.
function resendFormLevelError({ email, error }) {
  if (!error) return null;
  if (error.isNetworkError) {
    return `Could not reach the API to resend the invitation to ${email}. Check your connection and try again.`;
  }
  if (error.status === 404) {
    return `The invitation to ${email} no longer exists. Refresh the list to see the current state.`;
  }
  if (error.status === 409) {
    return error.detail || error.message || `The invitation to ${email} can’t be resent right now. Refresh the list and try again.`;
  }
  if (error.status === 500) {
    return error.detail
      ? `Resend to ${email} did not complete: ${error.detail}`
      : `Something went wrong resending to ${email}. Please try again.`;
  }
  return error.message || `Something went wrong resending to ${email}. Please try again.`;
}

function RevokeConfirmation({ invitation, isRevoking, revokeError, onCancel, onRevoke }) {
  const formError = revokeFormLevelError(revokeError);

  return (
    <>
      <p className="revoke-confirmation__lead">
        Revoke invitation for <strong>{invitation.email}</strong>?
      </p>
      <p className="revoke-confirmation__meta">
        {invitation.displayName || '(no display name)'}
        {' · '}{formatRole(invitation.roleKey)}
        {' · '}{invitation.clientName}
      </p>
      <p className="revoke-confirmation__warning">
        This will mark the invitation as revoked and clear its token. The
        recipient will no longer be able to redeem it. This action cannot
        be undone.
      </p>

      {formError && (
        <p className="form-error-summary" role="alert">{formError}</p>
      )}

      <div className="issue-form__actions">
        <button
          type="button"
          className="button-secondary"
          onClick={onCancel}
          disabled={isRevoking}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button-destructive"
          onClick={onRevoke}
          disabled={isRevoking}
        >
          {isRevoking ? (
            <>
              <Loader2 className="spinner" size={16} aria-hidden="true" />
              <span>Revoking…</span>
            </>
          ) : (
            <span>Revoke invitation</span>
          )}
        </button>
      </div>
    </>
  );
}

function ErrorCard({ error, onRetry }) {
  // ApiError carries isNetworkError when fetch itself failed (DNS,
  // CORS, offline) vs API errors where the server responded non-2xx.
  // Different copy for each so the user knows whether to check their
  // connection or wait for backend recovery.
  const isNetwork = !!(error && error.isNetworkError);
  const headline = isNetwork ? 'Could not reach the API' : 'Something went wrong';
  const detail = isNetwork
    ? 'Check your connection and try again.'
    : (error?.message || 'An unexpected error occurred.');

  return (
    <div className="error-card" role="alert">
      <div className="error-card__icon" aria-hidden="true">
        <AlertTriangle size={20} />
      </div>
      <div className="error-card__body">
        <p className="error-card__headline">{headline}</p>
        <p className="error-card__detail">{detail}</p>
      </div>
      <button
        type="button"
        className="retry-button"
        onClick={onRetry}
      >
        Retry
      </button>
    </div>
  );
}
